/**
 * "Alle brukere" — the full admin user directory (Steg D), rendered below the
 * pending-approvals queue on `/admin`. A searchable/paginated listing for
 * moderation context (who's approved, who's admin, which cookbooks they're in,
 * when last seen) plus, since abonnement Fase 1 / Slice 5, the SUBSCRIPTION
 * column and the only two subscription writes an admin has:
 * give a free Premium licence, and withdraw one.
 *
 * What this deliberately does NOT do (docs/desjeff-abonnement-arkitektur.md §4):
 *  - create or edit a PAYING subscription. That table is provider-owned and the
 *    API exposes no write path to it; a hand-edit would be overwritten by the
 *    next reconciliation. A `source: 'paid'` row therefore shows a note saying
 *    so instead of controls.
 *  - "revoke from date". `revoked_at` is a FACT, not a plan — a future value
 *    would withdraw the licence immediately — so scheduled expiry is
 *    `expiresAt` on the grant, and the form says as much.
 *
 * Search and pagination are both SERVER-SIDE (`GET /admin/users?q=&limit=&offset=`)
 * — this component never filters/sorts client-side, it only reflects whatever
 * page the server returns. The search box is debounced so fast typing doesn't
 * fire a request per keystroke; an `ignore` flag on the fetch effect (the same
 * pattern used by the pending queue / recipe editor) guards against a slower
 * older request clobbering a faster newer one — the LATEST query always wins.
 *
 * The licence mutations return the target's state AFTER the change, so a
 * successful grant/revoke PATCHES that single row in place — no refetch, and
 * the admin's search/page position survives the write.
 */

import type {
  AdminUserListResponse,
  AdminUserSubscription,
  SubscriptionStatus,
} from "@tjommi/shared";
import React, { useEffect, useState } from "react";

import {
  BadRequestError,
  ConflictError,
  ForbiddenError,
  NotFoundError,
  UnauthorizedError,
  fetchAllUsers,
  grantUserEntitlement,
  revokeUserEntitlement,
} from "../api";
import { formatDateTime } from "../lib/formatDate";

const PAGE_SIZE = 25;
/** Debounce delay before a keystroke in the search box triggers a refetch. */
const DEFAULT_SEARCH_DELAY_MS = 300;
/** Mirrors the DB CHECK `length(reason) BETWEEN 1 AND 200` (0019) — a courtesy bound, not the authority. */
const REASON_MAX_LENGTH = 200;
/** Columns in the table — the grant form spans all of them. */
const COLUMN_COUNT = 8;

type UsersState =
  | { status: "loading" }
  | { status: "ok"; data: AdminUserListResponse }
  | { status: "error"; message: string };

/**
 * §4's four display values. The API sends STRUCTURED fields and never a
 * pre-formatted label, so the Norwegian strings live here:
 *
 *   | Gratis             | plan === 'free'                                  |
 *   | Premium (gratis)   | source === 'granted' (reason shown alongside)    |
 *   | Premium (sagt opp) | source === 'paid' && paidCanceledWithAccess      |
 *   | Premium            | otherwise                                        |
 *
 * `plan` is the server's effective plan (one definition) — this only labels it.
 */
export function subscriptionLabel(subscription: AdminUserSubscription): {
  text: string;
  className: string;
} {
  if (subscription.plan === "free") return { text: "Gratis", className: "badge-free" };
  if (subscription.source === "granted")
    return { text: "Premium (gratis)", className: "badge-granted" };
  if (subscription.source === "paid" && subscription.paidCanceledWithAccess)
    return { text: "Premium (sagt opp)", className: "badge-canceled" };
  return { text: "Premium", className: "badge-premium" };
}

const PAID_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: "aktivt",
  past_due: "forfalt betaling",
  canceled: "sagt opp",
  expired: "utløpt",
};

/**
 * Turn the `<input type="date">` value into what the API wants.
 *
 * A blank field means a PERMANENT licence (`null`), which is why an
 * unparseable value must NOT collapse to `null` — silently making a mistyped
 * date permanent would be the worst possible reading of the admin's intent.
 * A chosen date is sent as the END of that day in local time, so "utløper
 * 1. januar" means the licence is valid THROUGH the 1st (and so that picking
 * today is a valid future timestamp rather than an instant 400).
 */
export function parseExpiryInput(value: string): { ok: true; iso: string | null } | { ok: false } {
  const trimmed = value.trim();
  if (trimmed === "") return { ok: true, iso: null };
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return { ok: false };
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59, 999);
  if (Number.isNaN(date.getTime())) return { ok: false };
  return { ok: true, iso: date.toISOString() };
}

/**
 * The subscription cell: the §4 badge plus the facts behind it. A licence
 * always shows its `reason` (that is the whole point of the field) and whether
 * it expires; a payment row is always shown too — including when a grant is
 * what actually decides the plan, since hiding half of that state would be the
 * bug (§4 calls the combination legitimate).
 */
function SubscriptionCell({
  subscription,
}: {
  subscription: AdminUserSubscription;
}): React.JSX.Element {
  const label = subscriptionLabel(subscription);
  const { grant, paid } = subscription;
  return (
    <>
      <span className={`badge ${label.className}`} data-testid="user-subscription">
        {label.text}
      </span>
      {grant && (
        <>
          <span className="sub-note" data-testid="user-grant-reason">
            Grunn: {grant.reason}
          </span>
          <span className="sub-note" data-testid="user-grant-expires">
            {grant.expiresAt ? `Utløper ${formatDateTime(grant.expiresAt)}` : "Varig"}
          </span>
        </>
      )}
      {paid && (
        <span className="sub-note" data-testid="user-paid-note">
          Betaling: {PAID_STATUS_LABELS[paid.status]}
          {paid.currentPeriodEnd ? ` til ${formatDateTime(paid.currentPeriodEnd)}` : ""} — styres av
          betalingsleverandøren, ikke av admin.
        </span>
      )}
    </>
  );
}

export function AllUsersSection({
  accessToken,
  onSessionExpired,
  searchDelayMs = DEFAULT_SEARCH_DELAY_MS,
}: {
  accessToken: string;
  onSessionExpired: () => void;
  /** Overridable in tests so the debounce doesn't slow the suite down. */
  searchDelayMs?: number;
}): React.JSX.Element {
  // `inputValue` is the raw controlled input; `query` is the debounced value
  // actually sent to the server. Kept separate so the input never stutters
  // while typing, and so a page change doesn't need to re-debounce.
  const [inputValue, setInputValue] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [state, setState] = useState<UsersState>({ status: "loading" });

  // Licence UI: which row's "gi gratis Premium" form is open, its fields, and
  // the in-flight/error state of the two writes. `rowError` is the revoke path
  // (there is no form to put it in); grant errors live inside the form.
  const [grantSubject, setGrantSubject] = useState<string | null>(null);
  const [grantReason, setGrantReason] = useState("");
  const [grantExpires, setGrantExpires] = useState("");
  const [grantError, setGrantError] = useState<string | null>(null);
  const [busySubject, setBusySubject] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  // Debounce: commit `inputValue` -> `query` after a pause, resetting to the
  // first page since a new search invalidates the current offset.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(inputValue.trim());
      setOffset(0);
    }, searchDelayMs);
    return () => clearTimeout(timer);
  }, [inputValue, searchDelayMs]);

  // Fetch the current page for the current (debounced) query.
  useEffect(() => {
    let ignore = false;
    setState({ status: "loading" });

    fetchAllUsers(accessToken, { q: query, limit: PAGE_SIZE, offset })
      .then((data) => {
        if (!ignore) setState({ status: "ok", data });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof UnauthorizedError) {
          onSessionExpired();
          return;
        }
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "Ukjent feil",
        });
      });

    return () => {
      ignore = true;
    };
  }, [accessToken, query, offset, onSessionExpired]);

  function handlePrev(limit: number): void {
    setOffset((prev) => Math.max(0, prev - limit));
  }

  function handleNext(limit: number): void {
    setOffset((prev) => prev + limit);
  }

  /**
   * Replace ONE row's subscription with the state the server just returned.
   * Both mutations answer with the post-change state precisely so this can be
   * done without a refetch — the admin keeps their search and page position.
   */
  function patchSubscription(subject: string, subscription: AdminUserSubscription): void {
    setState((prev) =>
      prev.status === "ok"
        ? {
            status: "ok",
            data: {
              ...prev.data,
              users: prev.data.users.map((u) =>
                u.subject === subject ? { ...u, subscription } : u,
              ),
            },
          }
        : prev,
    );
  }

  /** Map a failed licence write onto Norwegian copy. Returns null when handled by re-login. */
  function licenceErrorMessage(err: unknown, fallback: string): string | null {
    if (err instanceof UnauthorizedError) {
      onSessionExpired();
      return null;
    }
    if (err instanceof ConflictError) {
      // NOT the admin's mistake: it is a statement about the target's state.
      return (
        "Brukeren har allerede en aktiv gratis lisens. Slik endrer du en lisens: trekk den " +
        "tilbake, og gi deretter en ny med den grunnen eller utløpsdatoen du vil ha."
      );
    }
    if (err instanceof BadRequestError) {
      return (
        "Serveren avviste lisensen. Grunnen må være mellom 1 og 200 tegn, og en eventuell " +
        "utløpsdato må være fram i tid."
      );
    }
    if (err instanceof ForbiddenError) {
      return "Ingen tilgang — bare administratorer kan endre lisenser.";
    }
    if (err instanceof NotFoundError) {
      return "Fant ikke brukeren.";
    }
    return err instanceof Error ? err.message : fallback;
  }

  function openGrantForm(subject: string): void {
    setGrantSubject(subject);
    setGrantReason("");
    setGrantExpires("");
    setGrantError(null);
    setRowError(null);
  }

  function closeGrantForm(): void {
    setGrantSubject(null);
    setGrantReason("");
    setGrantExpires("");
    setGrantError(null);
  }

  function handleGrantSubmit(event: React.FormEvent, subject: string): void {
    event.preventDefault();

    // Client-side validation is a courtesy that saves a round trip — the
    // server's own check is the authoritative one, and its 400 is surfaced
    // below regardless of what we think of the input here.
    const reason = grantReason.trim();
    if (reason === "") {
      setGrantError("Grunn er påkrevd — skriv hvorfor denne brukeren skal ha gratis Premium.");
      return;
    }
    if (reason.length > REASON_MAX_LENGTH) {
      setGrantError(`Grunnen kan være maks ${REASON_MAX_LENGTH} tegn.`);
      return;
    }
    const expiry = parseExpiryInput(grantExpires);
    if (!expiry.ok) {
      setGrantError("Ugyldig utløpsdato. La feltet stå tomt for en varig lisens.");
      return;
    }
    if (expiry.iso !== null && new Date(expiry.iso).getTime() <= Date.now()) {
      setGrantError("Utløpsdatoen må være fram i tid.");
      return;
    }

    setGrantError(null);
    setRowError(null);
    setBusySubject(subject);
    grantUserEntitlement(subject, { reason, expiresAt: expiry.iso }, accessToken)
      .then((result) => {
        patchSubscription(result.subject, result.subscription);
        closeGrantForm();
      })
      .catch((err: unknown) => {
        const message = licenceErrorMessage(err, "Kunne ikke gi lisensen.");
        if (message) setGrantError(message);
      })
      .finally(() => setBusySubject(null));
  }

  function handleRevoke(subject: string, name: string): void {
    // Withdrawing a licence removes someone's access, immediately — ask first.
    const confirmed = window.confirm(
      `Trekke tilbake gratis Premium for ${name}?\n\n` +
        "Tilgangen faller bort umiddelbart. Skal lisensen i stedet opphøre av seg selv på en " +
        "gitt dato, avbryt her og gi lisensen på nytt med utløpsdato.",
    );
    if (!confirmed) return;

    setRowError(null);
    setBusySubject(subject);
    revokeUserEntitlement(subject, accessToken)
      .then((result) => {
        patchSubscription(result.subject, result.subscription);
      })
      .catch((err: unknown) => {
        const message = licenceErrorMessage(err, "Kunne ikke trekke tilbake lisensen.");
        if (message) setRowError(message);
      })
      .finally(() => setBusySubject(null));
  }

  return (
    <section style={{ marginTop: "40px" }} data-testid="all-users-section">
      <div className="feed-h">
        <h1>Alle brukere</h1>
        {state.status === "ok" && (
          <span className="feed-count" data-testid="users-total">
            {state.data.total} {state.data.total === 1 ? "bruker" : "brukere"}
          </span>
        )}
      </div>

      <div className="user-search-row">
        <label htmlFor="user-search-input" className="visually-hidden">
          Søk etter bruker
        </label>
        <input
          id="user-search-input"
          type="search"
          className="input"
          placeholder="Søk på navn eller e-post"
          data-testid="user-search"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
      </div>

      {state.status === "loading" && (
        <div className="feed-state" data-testid="users-loading">
          <p>Laster …</p>
        </div>
      )}

      {state.status === "error" && (
        <div className="feed-state feed-error" data-testid="users-error">
          <h2>Kunne ikke laste brukerlisten</h2>
          <p>{state.message}</p>
        </div>
      )}

      {state.status === "ok" && state.data.users.length === 0 && (
        <div className="feed-state feed-empty-state" data-testid="users-empty">
          {query === "" ? (
            <>
              <h2>Ingen brukere</h2>
              <p>Det finnes ingen registrerte brukere ennå.</p>
            </>
          ) : (
            <>
              <h2>Ingen treff</h2>
              <p>Ingen treff for «{query}».</p>
            </>
          )}
        </div>
      )}

      {state.status === "ok" && state.data.users.length > 0 && (
        <>
          {rowError && (
            <p className="feed-error" data-testid="users-row-error" style={{ padding: "12px" }}>
              {rowError}
            </p>
          )}
          <div className="table-scroll">
            <table className="users-table" data-testid="users-table">
              <thead>
                <tr>
                  <th scope="col">Navn</th>
                  <th scope="col">E-post</th>
                  <th scope="col">Status</th>
                  <th scope="col">Rolle</th>
                  <th scope="col">Abonnement</th>
                  <th scope="col">Kokebøker</th>
                  <th scope="col">Sist innlogget</th>
                  <th scope="col">Registrert</th>
                </tr>
              </thead>
              <tbody>
                {state.data.users.map((user) => {
                  const name = user.displayName ?? user.email ?? user.subject;
                  const busy = busySubject === user.subject;
                  const formOpen = grantSubject === user.subject;
                  const hasGrant = user.subscription.grant !== null;
                  return (
                    <React.Fragment key={user.subject}>
                      <tr data-testid={`user-row-${user.subject}`}>
                        <td>{name}</td>
                        <td>{user.email ?? "—"}</td>
                        <td>
                          <span
                            className={`badge ${user.approved ? "badge-approved" : "badge-pending"}`}
                            data-testid="user-status"
                          >
                            {user.approved ? "Godkjent" : "Venter"}
                          </span>
                        </td>
                        <td>
                          {user.isAdmin ? (
                            <span className="badge badge-admin" data-testid="user-role">
                              Admin
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <div className="sub-cell">
                            <SubscriptionCell subscription={user.subscription} />
                            {hasGrant ? (
                              <button
                                type="button"
                                className="btn btn-danger btn-tiny"
                                data-testid={`revoke-grant-${user.subject}`}
                                disabled={busy}
                                onClick={() => handleRevoke(user.subject, name)}
                              >
                                {busy ? "Trekker tilbake …" : "Trekk tilbake lisensen"}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-ghost btn-tiny"
                                data-testid={`grant-open-${user.subject}`}
                                disabled={busy || formOpen}
                                onClick={() => openGrantForm(user.subject)}
                              >
                                Gi gratis Premium
                              </button>
                            )}
                          </div>
                        </td>
                        <td>{user.cookbooks.length > 0 ? user.cookbooks.join(", ") : "—"}</td>
                        <td>{user.lastSeenAt ? formatDateTime(user.lastSeenAt) : "Aldri"}</td>
                        <td>{formatDateTime(user.createdAt)}</td>
                      </tr>

                      {formOpen && (
                        <tr data-testid={`grant-form-row-${user.subject}`}>
                          <td colSpan={COLUMN_COUNT} className="grant-cell">
                            <form
                              className="grant-form"
                              data-testid="grant-form"
                              onSubmit={(e) => handleGrantSubmit(e, user.subject)}
                            >
                              <h3 className="grant-form-title">Gi gratis Premium til {name}</h3>

                              <div className="grant-field">
                                <label htmlFor={`grant-reason-${user.subject}`}>
                                  Grunn (påkrevd)
                                </label>
                                <input
                                  id={`grant-reason-${user.subject}`}
                                  type="text"
                                  className="input"
                                  data-testid="grant-reason"
                                  placeholder="familie, pilotbruker, ansatt …"
                                  maxLength={REASON_MAX_LENGTH}
                                  aria-required="true"
                                  value={grantReason}
                                  disabled={busy}
                                  onChange={(e) => setGrantReason(e.target.value)}
                                />
                              </div>

                              <div className="grant-field">
                                <label htmlFor={`grant-expires-${user.subject}`}>
                                  Utløper (valgfritt)
                                </label>
                                <input
                                  id={`grant-expires-${user.subject}`}
                                  type="date"
                                  className="input"
                                  data-testid="grant-expires"
                                  value={grantExpires}
                                  disabled={busy}
                                  onChange={(e) => setGrantExpires(e.target.value)}
                                />
                              </div>

                              <p className="grant-hint" data-testid="grant-hint">
                                Tomt felt gir en varig lisens. Lisensen gjelder ut den valgte dagen.
                                Utløpsdato er også den eneste måten å planlegge slutten på: en
                                tilbaketrekking skjer alltid umiddelbart, den kan ikke dateres fram
                                i tid.
                              </p>

                              {grantError && (
                                <p className="feed-error" data-testid="grant-error">
                                  {grantError}
                                </p>
                              )}

                              <div className="grant-actions">
                                <button
                                  type="submit"
                                  className="btn btn-primary"
                                  data-testid="grant-submit"
                                  disabled={busy}
                                >
                                  {busy ? "Gir lisens …" : "Gi lisens"}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-ghost"
                                  data-testid="grant-cancel"
                                  disabled={busy}
                                  onClick={closeGrantForm}
                                >
                                  Avbryt
                                </button>
                              </div>
                            </form>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="users-pagination" data-testid="users-pagination">
            <button
              type="button"
              className="btn btn-ghost"
              data-testid="users-prev"
              disabled={offset === 0}
              onClick={() => handlePrev(state.data.limit)}
            >
              Forrige
            </button>
            <span className="users-pagination-status" data-testid="users-pagination-status">
              {state.data.total === 0
                ? "Viser 0 av 0"
                : `Viser ${offset + 1}–${Math.min(offset + state.data.limit, state.data.total)} av ${state.data.total}`}
            </span>
            <button
              type="button"
              className="btn btn-ghost"
              data-testid="users-next"
              disabled={offset + state.data.limit >= state.data.total}
              onClick={() => handleNext(state.data.limit)}
            >
              Neste
            </button>
          </div>
        </>
      )}
    </section>
  );
}
